import React from "react";
import SectionHeader from "components/Layout/SectionHeader";
import Section from "components/Layout/Section";
import { useTranslation } from "react-i18next";
import OdlAlert from "components/odl-v2/Alert/OdlAlert";
import Panel from "components/odl-v2/Layout/Panel";
import OutstandingApplicationCard from "pages/dashboard/OutstandingApplicationCard";
import { useFetchOutstandingApplicationsForCurrentUser } from "pages/dashboard/functions/useFetchOutstandingApplicationsForCurrentUser";
import isEmpty from "lodash/isEmpty";
import { usePaginatedData } from "hooks/usePaginatedData";
import { useStoreSelector } from "store/hooks";
import { selectIsLoadingThunk } from "store/app-state/loading/loading";
import { fetchPaginatedOutstandingApplicationsV3ForCurrentUser } from "store/domain-data/application-v3/applicationV3";
import LoadMoreButton from "components/LoadMoreButton/LoadMoreButton";
import NoApplicationCard from "components/ApplicationCard/NoApplicationCard";
import { Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { css } from "@emotion/react";
import PanelInner from "components/odl-v2/Layout/PanelInner";

type Props = {};
const OutstandingApplicationsSection: React.FC<Props> = () => {
  const { t } = useTranslation();

  const isLoadingApplications = useStoreSelector((state) =>
    selectIsLoadingThunk(state, fetchPaginatedOutstandingApplicationsV3ForCurrentUser)
  );

  const fetchApplicationsForPage = useFetchOutstandingApplicationsForCurrentUser();
  const { data: outstandingApplications, hasNextPage, loadNextPage } = usePaginatedData(fetchApplicationsForPage);

  const hasOutstandingApplications = !isEmpty(outstandingApplications);
  if (!isLoadingApplications && !hasOutstandingApplications) {
    return (
      <Panel hasInnerContainer={false}>
        <StyledPanelInner>
          <Typography variant={"h3"}>{t("You're all caught up")}</Typography>
          <Typography variant={"body2"}>{t("There are currently no actions required")}</Typography>
        </StyledPanelInner>
      </Panel>
    );
  }

  return (
    <Section data-testid={"OutStandingApplicationsSection"} header={<SectionHeader title={t(`Actions to be taken`)} />}>
      <OdlAlert header={t(`Some applications are suspended`)} severity={"info"}>
        {t(`One or more applications or consents are suspended because of outstanding RFIs or payments.`)}
      </OdlAlert>

      <Panel thickness={"thin"}>
        {isLoadingApplications && !hasOutstandingApplications && (
          <NoApplicationCard message={t("Loading outstanding applications")} />
        )}

        {hasOutstandingApplications && (
          <Stack>
            {outstandingApplications.map((outstandingApplication) => (
              <OutstandingApplicationCard applicationId={outstandingApplication.id} key={outstandingApplication.id} />
            ))}
          </Stack>
        )}
      </Panel>

      {hasNextPage && (
        <div>
          <LoadMoreButton isLoading={isLoadingApplications} onClick={loadNextPage} />
        </div>
      )}
    </Section>
  );
};

const StyledPanelInner = styled(PanelInner)(
  ({ theme }) => css`
    text-align: center;
    padding: ${theme.spacing(6)};
  `
);

export default OutstandingApplicationsSection;
